import React, { useState } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { UseAxios } from '../Server-Api-Instance';
import DatePicker from 'react-datepicker';
import theme from '../Shared/theme.js';
import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import {
  ThemeProvider,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
  CircularProgress,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import '../Shared/CommonStyle.css';
import { styled } from '@mui/system';
// import * as XLSX from 'xlsx';

const StyledArrowUpwardIcon = styled(ArrowUpwardIcon)({
  fontSize: '1.1em',
  marginLeft: '2px',
  color: 'white',
  '&:hover': {
    color: 'grey',
  },
  '&:active': {
    color: 'grey',
  },
});

const StyledArrowDownwardIcon = styled(ArrowDownwardIcon)({
  fontSize: '1.1em',
  marginLeft: '2px',
  color: 'white',
  '&:hover': {
    color: 'grey',
  },
  '&:active': {
    color: 'grey',
  },
});

const AgentCommissionTable = ({ username }) => {
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [dateError, setDateError] = useState('');
  const [data, setData] = useState([]);
  const [creditCardFeeData, setCreditCardFeeData] = useState(0); // For credit card fee data
  const [searchQuery, setSearchQuery] = useState('');
  const [sortedData, setSortedData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [noDataMessage, setNoDataMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const safeISODate = (date) => {
    if (!(date instanceof Date)) return '';
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const axios = UseAxios(false); // Pass true to use local URL

      // Fetch commission data
      const commissionResponse = await axios.post('/GetAgentCommissionDataReport', {
        username,
        startDate: safeISODate(startDate),
endDate: safeISODate(endDate),

      });

      const resultData = commissionResponse.data;
      const filteredData = resultData.filter((item) => item.BranchCode !== 'HQ');

      if (filteredData.length === 0) {
        setNoDataMessage('No data available for the selected Date Range.');
      } else {
        setNoDataMessage('');
      }
      setData(filteredData);
      setSortedData(filteredData);

      // Fetch credit card fee data
      const creditCardFeeResponse = await axios.post('/GetCreditCardFeeData', {
        username,
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
      });
      //TO show Total Amount
      // const creditCardFees = creditCardFeeResponse.data[0]?.CreditCardFee || 0;
      // setCreditCardFeeData(creditCardFees); // Store the credit card fee data

      const ccFeeData = creditCardFeeResponse.data || [];

      // Calculate total fee
      const totalCCFee = ccFeeData.reduce((acc, item) => acc + parseFloat(item.CreditCardFee || 0), 0);
      setCreditCardFeeData(totalCCFee);

      // Merge CC Fee into each booking (for ETE01 only)
      let mergedData = resultData.filter((item) => item.BranchCode !== 'HQ');

      if (username === 'ETE01') {
        mergedData = mergedData.map(item => {
          const cc = ccFeeData.find(f => f.FolderNumber === item.FolderNumber);
          return {
            ...item,
            creditCardFees: cc ? cc.CreditCardFee : 0
          };
        });
      }

      setData(mergedData);
      setSortedData(mergedData);

    } catch (error) {
      console.error('Error fetching data', error);
      setNoDataMessage('Error fetching data.');
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (!startDate || !endDate) {
      setDateError('Please select both start and end dates.');
      return;
    }

    if (startDate > endDate) {
      setDateError('Start date cannot be greater than end date.');
      return;
    }

    // Clear error and proceed
    setDateError('');
    if (!startDate || !endDate || startDate > endDate) return;
  fetchData();
  };


  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd/MM/yyyy');
  };

  const formatDecimal = (value) => {
    return parseFloat(value).toFixed(2);
  };


  const formatCurrency = (value) => {
    const formatted = new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
    }).format(value);
    return formatted.replace('£', '£');
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);

    if (query === '') {
      setSortedData(data);
    } else {
      const filteredData = data.filter((item) => {
        return Object.values(item).some((val) =>
          String(val).toLowerCase().includes(query.toLowerCase())
        );
      });
      setSortedData(filteredData);
    }
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }

    const sortedArray = [...sortedData].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === 'ascending' ? -1 : 1;
      }
      if (a[key] > b[key]) {
        return direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });

    setSortedData(sortedArray);
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction === 'ascending' ? (
      <StyledArrowUpwardIcon />
    ) : (
      <StyledArrowDownwardIcon />
    );
  };

  // const exportToExcel = () => {
  //   const dataToExport = sortedData.map(({ BookingRef, ...rest }) => rest);

  //   // Calculate footer values
  //   const totalOutstandingBalance = sortedData.reduce((acc, row) => acc + parseFloat(row.OutstandingBalance || 0), 0);
  //   const totalSellAmount = sortedData.reduce((acc, row) => acc + parseFloat(row.SellAmount || 0), 0);
  //   const totalAgencyCommission = sortedData.reduce((acc, row) => acc + parseFloat(row.AgencyCommission || 0), 0);
  //   const totalJTACommission = sortedData.reduce((acc, row) => acc + parseFloat(row.JTACommission || 0), 0);
  //   const totalPaid = totalAgencyCommission - parseFloat(creditCardFeeData || 0);

  //   // Append footer data as a new row to dataToExport
  //   const footerRow = {
  //     BranchCode: '', // Leave empty or add a label
  //     AgentName: 'Totals',
  //     LeadContact: '',
  //     FolderNumber: '',
  //     CampaignCode: '',
  //     DepartureDate: '',
  //     BookDate: '',
  //     BalanceDueDate: '',
  //     OutstandingBalance: totalOutstandingBalance.toFixed(2),
  //     SellAmount: `£ ${totalSellAmount.toFixed(2)}`,
  //     AgencyCommission: `£ ${totalAgencyCommission.toFixed(2)}`,
  //     JTACommission: `£ ${totalJTACommission.toFixed(2)}`,
  //   };

  //   const creditCardFeeRow = {
  //     BranchCode: '',
  //     AgentName: 'Total Credit Card Fees',
  //     OutstandingBalance: '',
  //     SellAmount: '',
  //     AgencyCommission: `£ ${creditCardFeeData.toFixed(2)}`,
  //     JTACommission: '',
  //   };

  //   const totalPaidRow = {
  //     BranchCode: '',
  //     AgentName: 'Total Paid',
  //     OutstandingBalance: '',
  //     SellAmount: '',
  //     AgencyCommission: `£ ${totalPaid.toFixed(2)}`,
  //     JTACommission: '',
  //   };

  //   // Push footer rows at the end of the data
  //   dataToExport.push(footerRow, creditCardFeeRow, totalPaidRow);

  //   const ws = XLSX.utils.json_to_sheet(dataToExport);
  //   const wb = XLSX.utils.book_new();
  //   XLSX.utils.book_append_sheet(wb, ws, 'Agent Commission Report');
  //   XLSX.writeFile(wb, 'AgentCommissionReport.xlsx');
  // };
  const exportToExcel = async () => {
    // Create a new workbook and a worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Agent Commission Report');

    // Add header row
    const headers = [
      'Branch Code', 'Agent Name', 'Lead Contact', 'Folder Number', 'Campaign Code',
      'Package Type', 'Departure Date', 'Book Date', 'Balance Due Date',
      'Sell Amount', 'Agency Commission', 'JTA Commission'

    ];
    // Only include "CC Fees" column if username is ETE01
    if (username === 'ETE01') {
      headers.push('CC Fees');
    }
    worksheet.addRow(headers);

    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB'); // Formats to DD/MM/YYYY
    };

    // Add data rows
    sortedData.forEach(row => {

      const rowData = [
        row.BranchCode,
        row.AgentName,
        row.LeadContact,
        row.FolderNumber,
        row.CampaignCode,
        row.PackageType,
        formatDate(row.DepartureDate),
        formatDate(row.BookDate),
        formatDate(row.BalanceDueDate),
        // parseFloat(row.OutstandingBalance).toFixed(2),
        `£ ${parseFloat(row.SellAmount).toFixed(2)}`,
        `£ ${parseFloat(row.AgencyCommission).toFixed(2)}`,
        `£ ${parseFloat(row.JTACommission).toFixed(2)}`,
        //  `£ ${parseFloat(row.creditCardFees).toFixed(2)}`
      ];

      // Only add per-row CC Fees for ETE01
      if (username === 'ETE01') {
        const ccFee = Number(row.creditCardFees || 0);
        rowData.push(`£ ${ccFee.toFixed(2)}`);
      }

      worksheet.addRow(rowData);
    });

    // Add footer rows with totals
    // const totalOutstandingBalance = sortedData.reduce((acc, row) => acc + parseFloat(row.OutstandingBalance || 0), 0);
    const totalSellAmount = sortedData.reduce((acc, row) => acc + parseFloat(row.SellAmount || 0), 0);
    const totalAgencyCommission = sortedData.reduce((acc, row) => acc + parseFloat(row.AgencyCommission || 0), 0);
    const totalJTACommission = sortedData.reduce((acc, row) => acc + parseFloat(row.JTACommission || 0), 0);
    const totalPaid = totalAgencyCommission - parseFloat(creditCardFeeData || 0);

    // Footer row 1: Totals
    const footerRow = worksheet.addRow([
      '', '', '', '', '', '', '', '', 'Totals',
      // totalOutstandingBalance.toFixed(2),
      `£ ${totalSellAmount.toFixed(2)}`,
      `£ ${totalAgencyCommission.toFixed(2)}`,
      `£ ${totalJTACommission.toFixed(2)}`
    ]);

    // Footer row 2: Credit Card Fees
    // const creditCardFeeRow = worksheet.addRow([
    //   '', '', '', '', '', '', '','', 'Total Credit Card Fees',  '', 
    //   `£ ${creditCardFeeData.toFixed(2)}`
    // ]);
    const label =
      username === 'ETE01'
        ? 'Total CC Fees'
        : 'Total CC Fees by payment created date';

    const creditCardFeeRow = worksheet.addRow([
      '', '', '', '', '', '', '', '', label, '',
      `£ ${creditCardFeeData.toFixed(2)}`
    ]);
    creditCardFeeRow.getCell(9).alignment = { wrapText: true };
    // Footer row 3: Total Paid
    const totalPaidRow = worksheet.addRow([
      '', '', '', '', '', '', '', '', 'Total Paid', '',
      `£ ${totalPaid.toFixed(2)}`, '',
    ]);

    // Style footer rows (color, bold, etc.)
    // [footerRow, creditCardFeeRow, totalPaidRow].forEach(row => {
    //   row.eachCell(cell => {
    //     cell.font = { bold: true };
    //     cell.fill = {
    //       type: 'pattern',
    //       pattern: 'solid',
    //       fgColor: { argb: 'FFD700' }, // Gold color
    //     };
    //     cell.font = {
    //       bold: true,
    //       color: { argb: 'FF0000' }, // Red text color
    //     };
    //   });
    // });
    // Style 'Total' row (green text)
    footerRow.eachCell(cell => {
      cell.font = {
        bold: true,
        color: { argb: '008000' }, // Green text color
      };
    });

    // Style 'Credit Card Fee' row (red text)
    creditCardFeeRow.eachCell(cell => {
      cell.font = {
        bold: true,
        color: { argb: 'FF0000' }, // Red text color
      };
    });

    // Style 'Total Paid' row (blue text with highlight)
    totalPaidRow.eachCell(cell => {
      cell.font = {
        bold: true,
        color: { argb: '0000FF' }, // Blue text color
      };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD700' },
      };
    });


    // Style header row
    worksheet.getRow(1).eachCell(cell => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '808080' }, // Gray color for header
      };

      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Set column width
    worksheet.columns.forEach(column => {
      column.width = 20;
    });


    const excelStartDate = formatDate(startDate);
    const excelEndDate = formatDate(endDate);

    // Generate the file name including the dates
    const filename = `${username}_AgentCommissionReport_from_${excelStartDate}_to_${excelEndDate}.xlsx`;

    // Generate Excel file and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), filename);

    // const buffer = await workbook.xlsx.writeBuffer();
    // saveAs(new Blob([buffer]),  `${username} AgentCommissionReport.xlsx`);
  };


  const isSubmitDisabled = !startDate || !endDate || startDate > endDate;

  return (
    <ThemeProvider theme={theme}>
      <Box className="heading-box">
        AGENCY COMMISSION REPORT
      </Box>
      <form onSubmit={handleSubmit}>
        <div className="form">
          <Typography variant="subtitle1" className="bold-label">
            Start Date:
          </Typography>
          {/* <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            customInput={<TextField variant="outlined" className="date-input" />}
            dateFormat="dd/MM/yyyy"
          /> */}
          <TextField
            label="Departure Start Date"
            type="date"
            value={safeISODate(startDate)}
            onChange={(e) => {
              const value = e.target.value;
              setStartDate(value ? new Date(value) : null);
            }} InputLabelProps={{ shrink: true }}
          />
          <Typography variant="subtitle1" className="bold-label">
            End Date:
          </Typography>
          {/* <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            customInput={<TextField variant="outlined" className="date-input" />}
            dateFormat="dd/MM/yyyy"
          /> */}
          <TextField
            label="Departure End Date"
            type="date"
            value={safeISODate(endDate)}
            onChange={(e) => {
              const value = e.target.value;
              setEndDate(value ? new Date(value) : null);
            }}
            InputLabelProps={{ shrink: true }}
          />
          {dateError && (
            <Typography variant="body2" color="error" align="center">
              {dateError}
            </Typography>
          )}

          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearch}
            className="search-input"
          />
          <Button type="submit" variant="contained" style={{ color: '#fff' }}
            className="fetch-button" disabled={isSubmitDisabled}>
            SUBMIT
          </Button>
          <Button
            variant="contained"
            disabled={sortedData.length === 0}
            className="fetch-button"
            onClick={exportToExcel}
            style={{ marginLeft: '10px', color: '#fff' }}

          >
            Export to Excel
          </Button>
        </div>
        {isSubmitDisabled && (
          <Typography variant="body2" color="error" align="center">
            {(!startDate || !endDate) ? 'Please select both start and end date.' : 'Start date cannot be greater than end date.'}
          </Typography>
        )}
      </form>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
          <CircularProgress />
        </Box>
      ) : (
        <React.Fragment>
          {data.length > 0 ? (
            <TableContainer component={Paper} className="table">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ textAlign: 'center' }} onClick={() => handleSort('BranchCode')}>
                      Branch Code {renderSortIcon('BranchCode')}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', width: '12%', minWidth: '15%' }} onClick={() => handleSort('AgentName')} >
                      Agent Name {renderSortIcon('AgentName')}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', width: '10%' }} onClick={() => handleSort('LeadContact')}>
                      Lead Contact {renderSortIcon('LeadContact')}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }} onClick={() => handleSort('FolderNumber')}>
                      Folder Number {renderSortIcon('FolderNumber')}
                    </TableCell>
                    {/* <TableCell onClick={() => handleSort('BookingRef')} style={{ fontWeight: 'bold' }}>
                      Booking Ref {renderSortIcon('BookingRef')}
                    </TableCell> */}
                    <TableCell style={{ textAlign: 'center' }} onClick={() => handleSort('CampaignCode')} >
                      Campaign Code {renderSortIcon('CampaignCode')}
                    </TableCell>
                    <TableCell style={{ textAlign: 'center' }} onClick={() => handleSort('PackageType')} >
                      Package Type {renderSortIcon('PackageType')}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }} onClick={() => handleSort('DepartureDate')} >
                      Departure Date {renderSortIcon('DepartureDate')}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }} onClick={() => handleSort('BookDate')} >
                      Book Date {renderSortIcon('BookDate')}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }} onClick={() => handleSort('BalanceDueDate')} >
                      Balance Due Date {renderSortIcon('BalanceDueDate')}
                    </TableCell>
                    {/* <TableCell onClick={() => handleSort('OutstandingBalance')} >
                      Outstanding Balance {renderSortIcon('OutstandingBalance')}
                    </TableCell> */}
                    <TableCell style={{ textAlign: 'right', width: '10%' }} onClick={() => handleSort('SellAmount')}>
                      Sell Amount {renderSortIcon('SellAmount')}
                    </TableCell>
                    {/* <TableCell onClick={() => handleSort('BrochureCodes')} style={{ fontWeight: 'bold' }}>
                    BrochureCodes{renderSortIcon('BrochureCodes')}
                    </TableCell> */}
                    <TableCell style={{ textAlign: 'right' }} onClick={() => handleSort('AgencyCommission')} >
                      Agency Commission {renderSortIcon('AgencyCommission')}

                    </TableCell>

                    {/* ✅ Show CC Fees column only for ETE01 */}
                    {username === 'ETE01' && (
                      <TableCell style={{ textAlign: 'right' }} onClick={() => handleSort('creditCardFees')}>
                        CC Fees {renderSortIcon('creditCardFees')}
                      </TableCell>
                    )}
                    {/* <TableCell onClick={() => handleSort('JTACommission')}>
                      JTA Commission {renderSortIcon('JTACommission')}

                    </TableCell> */}

                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.map((row, index) => (
                    <TableRow key={row.FolderNumber}>
                      <TableCell style={{ textAlign: 'center' }}>{row.BranchCode}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{row.AgentName}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{row.LeadContact}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{row.FolderNumber}</TableCell>
                      {/* <TableCell>{row.BookingRef}</TableCell> */}
                      <TableCell style={{ textAlign: 'center' }}>{row.CampaignCode}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{row.PackageType}</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>{formatDate(row.DepartureDate)}</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>{formatDate(row.BookDate)}</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>{formatDate(row.BalanceDueDate)}</TableCell>
                      {/* <TableCell>{formatDecimal(row.OutstandingBalance)}</TableCell> */}
                      <TableCell style={{ textAlign: 'right' }}> {formatCurrency(row.SellAmount)}</TableCell>
                      {/* <TableCell> {row.BrochureCodes}</TableCell> */}
                      <TableCell style={{ textAlign: 'right' }}> {formatCurrency(row.AgencyCommission)}</TableCell>
                      {/* <TableCell> {formatCurrency(row.JTACommission)}</TableCell> */}
                      {/* ✅ Show CC Fees only for ETE01 */}
                      {username === 'ETE01' && (
                        <TableCell style={{ textAlign: 'right' }}>
                          {formatCurrency(row.creditCardFees || 0)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
                <tfoot>
                  <TableRow>
                    {/* Total Paid */}
                    {username === 'ETE01' && (<TableCell colSpan={10} style={{ fontWeight: 'bold', textAlign: 'left', paddingLeft: '30px' }}>Total</TableCell>)}
                    {username != 'ETE01' && (<TableCell colSpan={9} style={{ fontWeight: 'bold', textAlign: 'left', paddingLeft: '30px' }}>Total</TableCell>)}
                    {/* Outstanding Balance */}
                    {/* <TableCell style={{ fontWeight: 'bold' }}>
                      £ {formatDecimal(sortedData.reduce((acc, row) => acc + parseFloat(row.OutstandingBalance || 0), 0))}
                    </TableCell> */}

                    {/* Sell Amount */}
                    <TableCell style={{ fontWeight: 'bold', textAlign: 'right', textAlign: 'right' }}>
                      {formatCurrency(sortedData.reduce((acc, row) => acc + parseFloat(row.SellAmount || 0), 0))}
                    </TableCell>

                    {/* Agency Commission */}
                    <TableCell style={{ fontWeight: 'bold', color: 'green', textAlign: 'right' }}>
                      {formatCurrency(sortedData.reduce((acc, row) => acc + parseFloat(row.AgencyCommission || 0), 0))}
                    </TableCell>
                    {/* ✅ Only show this column for ETE01 */}
                    {/* {username === 'ETE01' && (
                      <TableCell style={{ fontWeight: 'bold' }}>
                        {formatCurrency(sortedData.reduce((acc, row) => acc + parseFloat(row.creditCardFees || 0), 0))}
                      </TableCell>
                    )} */}
                    {/* JTA Commission */}
                    {/* <TableCell style={{ fontWeight: 'bold' }}>
                       {formatCurrency(sortedData.reduce((acc, row) => acc + parseFloat(row.JTACommission || 0), 0))}
                    </TableCell> */}
                  </TableRow>

                  <TableRow>
                    {/* <TableCell colSpan={7}></TableCell> */}

                    {/* <TableCell colSpan={3} style={{ fontWeight: 'bold', color: 'red' }}>
  {username === 'ETE01' 
    ? 'Total CC Fees' 
    : 'Based on Booking Created/Taken CC Fees'} 
  &nbsp;&nbsp;&nbsp;&nbsp; - &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
  {formatCurrency(creditCardFeeData)}
</TableCell> */}

                    {username === 'ETE01' && (<TableCell colSpan={8}></TableCell>)}
                    {username != 'ETE01' && (<TableCell colSpan={7}></TableCell>)}
                    {/* Total Credit Card Fee */}
                    {/* Label cell */}
                    <TableCell colSpan={3} style={{ fontWeight: 'bold', color: 'red', textAlign: 'right' }}>
                      {username === 'ETE01'
                        ? 'Total CC Fees :'
                        : 'Total CC Fees by payment created date :'}
                    </TableCell>

                    {/* Amount cell */}
                    <TableCell style={{ fontWeight: 'bold', color: 'red', textAlign: 'right' }}>
                      {formatCurrency(creditCardFeeData)}
                    </TableCell>

                  </TableRow>
                  {username === 'ETE01' && (<TableCell colSpan={9}></TableCell>)}
                  {username != 'ETE01' && (<TableCell colSpan={8}></TableCell>)}
                  {/* Total Paid Calculation */}
                  <TableCell colSpan={2} style={{ fontWeight: 'bold', color: 'blue', textAlign: 'right', textAlign: 'right' }}>
                    Total Payable :
                  </TableCell>
                  <TableCell style={{ fontWeight: 'bold', color: 'blue', textAlign: 'right' }}>
                    {formatCurrency(
                      sortedData.reduce((acc, row) => acc + parseFloat(row.AgencyCommission || 0), 0) - parseFloat(creditCardFeeData || 0)
                    )}  </TableCell>


                </tfoot>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="h6" align="center">
              {noDataMessage && (
                <div style={{ color: 'red' }}>
                  {noDataMessage}
                </div>
              )}
            </Typography>
          )}
        </React.Fragment>
      )}
    </ThemeProvider>
  );
};

export default AgentCommissionTable;
